import { useState } from 'react';
import { Footer } from '../components/Footer';
import { useAuth } from '../lib/auth';

// Support requests are forwarded to this inbox. We use FormSubmit.co (no signup,
// no backend) to relay the form to email — the Studio is a static SPA and the
// backend has no mail infrastructure. NOTE: FormSubmit requires a one-time
// activation: the first real submission triggers a confirmation email to
// SUPPORT_EMAIL; click its link once and every later submission flows through.
const SUPPORT_EMAIL = 'riffloopapp@gmail.com';
const FORM_ENDPOINT = `https://formsubmit.co/ajax/${SUPPORT_EMAIL}`;

type Status = 'idle' | 'sending' | 'sent' | 'error';

export function Support() {
  const { user } = useAuth();
  const [name, setName] = useState(user?.name ?? '');
  const [email, setEmail] = useState(user?.email ?? '');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState('');

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!message.trim() || !email.trim()) return;
    setStatus('sending');
    setError('');
    try {
      const res = await fetch(FORM_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          name: name || '(not given)',
          email,
          _subject: subject ? `Riffloop support: ${subject}` : 'Riffloop support request',
          _template: 'table',
          plan: user?.plan ?? 'anonymous',
          message,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && (data.success === 'true' || data.success === true)) {
        setStatus('sent');
      } else {
        throw new Error(data.message || 'Could not send your message.');
      }
    } catch (err: any) {
      setError(err?.message || 'Something went wrong. Please email us directly.');
      setStatus('error');
    }
  }

  return (
    <div className="rl-page" style={{ maxWidth: 640 }}>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, margin: '0 0 6px' }}>Support</h1>
        <p style={{ color: 'var(--text-dim)', fontSize: 14, margin: 0, lineHeight: 1.55 }}>
          Question, bug, or feedback? Send us a message and we'll get back to you by email.
        </p>
      </div>

      {status === 'sent' ? (
        <div className="rl-card" style={{ textAlign: 'center', padding: 32 }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>✓</div>
          <h3 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 700 }}>Message sent</h3>
          <p style={{ color: 'var(--text-dim)', fontSize: 14, margin: '0 0 18px', lineHeight: 1.55 }}>
            Thanks for reaching out — we'll reply to <strong>{email}</strong> as soon as we can.
          </p>
          <button
            className="rl-btn-secondary"
            onClick={() => {
              setSubject('');
              setMessage('');
              setStatus('idle');
            }}
          >
            Send another
          </button>
        </div>
      ) : (
        <form className="rl-card" onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <label style={{ flex: '1 1 200px' }}>
              <div className="rl-label" style={{ marginBottom: 6 }}>
                Name
              </div>
              <input
                className="rl-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                autoComplete="name"
              />
            </label>
            <label style={{ flex: '1 1 200px' }}>
              <div className="rl-label" style={{ marginBottom: 6 }}>
                Email
              </div>
              <input
                className="rl-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                autoComplete="email"
              />
            </label>
          </div>

          <label>
            <div className="rl-label" style={{ marginBottom: 6 }}>
              Subject
            </div>
            <input
              className="rl-input"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="What's this about?"
            />
          </label>

          <label>
            <div className="rl-label" style={{ marginBottom: 6 }}>
              Message
            </div>
            <textarea
              className="rl-input"
              required
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Tell us what's going on…"
              rows={6}
              style={{ resize: 'vertical', minHeight: 120, lineHeight: 1.5 }}
            />
          </label>

          {status === 'error' && (
            <div style={{ color: '#fca5a5', fontSize: 13 }}>
              {error}{' '}
              <a href={`mailto:${SUPPORT_EMAIL}`} style={{ color: 'var(--accent-purple)', textDecoration: 'underline' }}>
                Email us directly
              </a>
              .
            </div>
          )}

          <button
            type="submit"
            className="rl-btn-primary"
            disabled={status === 'sending'}
            style={{ alignSelf: 'flex-start', padding: '11px 22px' }}
          >
            {status === 'sending' ? 'Sending…' : 'Send message'}
          </button>

          <p style={{ color: 'var(--text-faint)', fontSize: 12, margin: '4px 0 0' }}>
            Prefer email? Reach us at{' '}
            <a href={`mailto:${SUPPORT_EMAIL}`} style={{ color: 'var(--text-dim)', textDecoration: 'underline' }}>
              {SUPPORT_EMAIL}
            </a>
            .
          </p>
        </form>
      )}

      <Footer compact />
    </div>
  );
}
